'use client';

import { UserProfile } from '@clerk/clerk-react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ShieldCheck } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

export const Route = createFileRoute('/session-tasks/setup-mfa')({
  component: SetupMfaPage,
});

/**
 * Session task page for MFA setup.
 *
 * Users are redirected here by Clerk when MFA is required but not yet configured.
 * This page is outside the `_authenticated` layout because the user's session
 * may have a pending `setup-mfa` task that blocks full access.
 */
function SetupMfaPage() {
  const navigate = useNavigate();

  const handleContinue = () => {
    void navigate({ to: '/dashboard' });
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-xl" data-testid="setup-mfa-page">
        <CardHeader className="text-center">
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <ShieldCheck className="h-6 w-6 text-primary" aria-hidden="true" />
          </div>
          <CardTitle className="text-2xl" data-testid="setup-mfa-title">
            Set Up Multi-Factor Authentication
          </CardTitle>
          <p className="mt-2 text-sm text-muted-foreground">
            Your organization requires multi-factor authentication. Please set up an authenticator
            app to continue using Quantifier.
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="overflow-hidden rounded-lg border">
            <UserProfile
              routing="virtual"
              appearance={{
                elements: {
                  rootBox: 'w-full',
                  card: 'shadow-none border-0',
                  navbar: 'hidden',
                  navbarMobileMenuButton: 'hidden',
                  pageScrollBox: 'p-0',
                },
              }}
            />
          </div>

          <div className="flex justify-center">
            <Button
              onClick={handleContinue}
              size="lg"
              data-testid="setup-mfa-continue-button"
            >
              Continue to Quantifier
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
